import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  Button,
  Space,
  message,
  Drawer,
  Input,
  Select,
  Modal,
  Tag,
  Tooltip,
} from "antd";
import {
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  MailOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import PermissionRender from "../components/PermissionRender";
import {
  listDeletionRequests,
  approveDeletionRequest,
  rejectDeletionRequest,
} from "../api/parkpass/accountDeletion";

const { Option } = Select;

const STATUS_COLORS = {
  pending: "orange",
  approved: "cyan",
  rejected: "red",
  // cancelled: "default",
  // processed: "blue",
};

// Human-readable labels that reflect the actual workflow state
const STATUS_LABELS = {
  pending: "En attente de l'agent",
  approved: "Rétention 90 jours",
  rejected: "Rejeté",
  cancelled: "Annulé",
  processed: "Purgé",
};

function AccountDeletionRequestsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [adminNote, setAdminNote] = useState("");

  useEffect(() => {
    fetchItems();
  }, [statusFilter]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const data = await listDeletionRequests(params);
      // If API returns paginated result with items key, handle that; otherwise assume array
      const list = Array.isArray(data) ? data : data.items || data.data || [];
      setItems(list.map((it) => ({ key: it.id, ...it })));
    } catch (err) {
      message.error("Impossible de charger les demandes");
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (record) => {
    setSelected(record);
    setAdminNote(record.admin_note || "");
    setDrawerVisible(true);
  };

  const handleApprove = async (record) => {
    Modal.confirm({
      title: "Approuver la demande ?",
      content: "Voulez-vous approuver et planifier la suppression ?",
      onOk: async () => {
        try {
          await approveDeletionRequest(record.id, { admin_note: adminNote });
          message.success("Demande approuvée");
          setDrawerVisible(false);
          fetchItems();
        } catch (err) {
          message.error("Échec de l'approbation");
        }
      },
    });
  };

  const handleReject = async (record) => {
    Modal.confirm({
      title: "Rejeter la demande ?",
      content: "Voulez-vous rejeter la demande de suppression ?",
      onOk: async () => {
        try {
          await rejectDeletionRequest(record.id, { admin_note: adminNote });
          message.success("Demande rejetée");
          setDrawerVisible(false);
          fetchItems();
        } catch (err) {
          message.error("Échec du rejet");
        }
      },
    });
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 50,
    },
    {
      title: "Utilisateur",
      dataIndex: "user",
      key: "user",
      width: 180,
      render: (user) =>
        user ? `${user.first_name + " " + user.last_name}` : "-",
    },
    {
      title: (
        <Tooltip title="Adresse e-mail saisie lors de la soumission de la demande. La valeur dans le compte est maintenant masquée.">
          <span>
            <MailOutlined className="mr-1" />
            Email original
          </span>
        </Tooltip>
      ),
      dataIndex: "original_email",
      key: "original_email",
      width: 230,
      ellipsis: true,
      render: (email) => email || <span className="text-gray-400">—</span>,
    },
    {
      title: "Motif",
      dataIndex: "reason",
      key: "reason",
      ellipsis: true,
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 160,
      render: (s) => (
        <Tag color={STATUS_COLORS[s] || "default"}>
          {STATUS_LABELS[s] || s}
        </Tag>
      ),
    },
    {
      title: "Demandé le",
      dataIndex: "requested_at",
      key: "requested_at",
      width: 150,
    },
    {
      title: (
        <Tooltip title="Date à laquelle la purge définitive des données sera exécutée (90 jours après approbation).">
          <span>
            <CalendarOutlined className="mr-1" />
            Date de purge
          </span>
        </Tooltip>
      ),
      dataIndex: "scheduled_at",
      key: "scheduled_at",
      width: 150,
      render: (date) => date || <span className="text-gray-400">—</span>,
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      align: "right",
      render: (_, record) => (
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => openDetails(record)}>
            Détails
          </Button>
          {/* <PermissionRender
            requiredPermission={["citizen_delete_request", "review"]}
          >
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => handleApprove(record)}
              disabled={record.status !== "pending"}
            >
              Approuver
            </Button>
            <Button
              danger
              icon={<CloseOutlined />}
              onClick={() => handleReject(record)}
              disabled={record.status !== "pending"}
            >
              Rejeter
            </Button>
          </PermissionRender> */}
        </Space>
      ),
    },
  ];

  return (
    <div className="min-h-screen">
      <div className="py-6 sm:px-6 lg:px-8">
        <Row gutter={16} className="mb-4">
          <Col xs={24} sm={12} lg={6}>
            <Select
              placeholder="Filtrer par statut"
              value={statusFilter}
              onChange={(val) => setStatusFilter(val)}
              allowClear
              className="w-full"
            >
              <Option value="pending">Pending</Option>
              <Option value="approved">Approved</Option>
              <Option value="rejected">Rejected</Option>
              <Option value="cancelled">Cancelled</Option>
              <Option value="processed">Processed</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} lg={6} className="flex justify-end">
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchItems}>
                Rafraîchir
              </Button>
            </Space>
          </Col>
        </Row>

        <Card className="shadow-sm">
          <Table
            dataSource={items}
            columns={columns}
            scroll={{ x: 1600 }}
            loading={loading}
            pagination={{ defaultPageSize: 10, showSizeChanger: true }}
          />
        </Card>

        <Drawer
          title={`Demande #${selected?.id || ""}`}
          placement="right"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          width={560}
        >
          {selected && (
            <div>
              <p>
                <strong>Utilisateur:</strong>{" "}
                {selected.user
                  ? selected.user.first_name + " " + selected.user.last_name
                  : "—"}
              </p>
              <p>
                <strong>Email original:</strong>{" "}
                {selected.original_email || (
                  <span className="text-gray-400">
                    Non disponible (demande antérieure à la mise à jour)
                  </span>
                )}
              </p>
              <p>
                <strong>Motif:</strong> {selected.reason || "—"}
              </p>
              <p>
                <strong>Demandé le:</strong> {selected.requested_at || "—"}
              </p>
              <p>
                <strong>Statut:</strong>{" "}
                <Tag color={STATUS_COLORS[selected.status] || "default"}>
                  {STATUS_LABELS[selected.status] || selected.status}
                </Tag>
              </p>
              {selected.status === "pending" && (
                <p className="text-orange-600 text-sm bg-orange-50 p-2 rounded">
                  ⚠️ Le compte citoyen est déjà verrouillé. L'email original a
                  été libéré pour réinscription. Approuver démarre la rétention
                  légale de 90 jours. Rejeter restaure l'accès.
                </p>
              )}
              {selected.scheduled_at && (
                <p>
                  <strong>Date de purge définitive:</strong>{" "}
                  {selected.scheduled_at}
                </p>
              )}
              {selected.approved_at && (
                <p>
                  <strong>Approuvé le:</strong> {selected.approved_at}
                </p>
              )}

              <p className="mt-4">
                <strong>Note admin:</strong>
              </p>
              <Input.TextArea
                rows={4}
                value={adminNote}
                disabled={selected.status !== "pending"}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Ajouter une note pour l'approbation / rejet"
              />

              <div className="mt-4 flex justify-end space-x-2">
                <PermissionRender
                  requiredPermission={["citizen_delete_request", "review"]}
                >
                  <Button
                    type="primary"
                    onClick={() => handleApprove(selected)}
                    disabled={selected.status !== "pending"}
                  >
                    <CheckOutlined /> Approuver
                  </Button>
                  <Button
                    danger
                    onClick={() => handleReject(selected)}
                    disabled={selected.status !== "pending"}
                  >
                    <CloseOutlined /> Rejeter
                  </Button>
                </PermissionRender>
                <Button onClick={() => setDrawerVisible(false)}>Fermer</Button>
              </div>
            </div>
          )}
        </Drawer>
      </div>
    </div>
  );
}

export default AccountDeletionRequestsPage;
